import { SecondaryTabs, TableV2 as Table } from "@asayinc/component-library";
import { Stack } from "@mui/material";
import {
  FilterList,
  Downloading,
  DownloadingOutlined,
  History,
  HistoryOutlined,
  Verified,
  VerifiedOutlined,
  WatchLater,
  WatchLaterOutlined,
} from "@mui/icons-material";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { TableMultiSelectFilter } from "src/components/molecules/TableMultiSelectFilter";
import { ROW_OPTIONS } from "src/constants/options/rowOptions";
import { SHARE_RANGE_ANALYSIS_TYPE_OPTIONS } from "src/constants/options/shareRangeAnalysisOptions";
import { useTablePagination } from "src/hooks/useTablePagination";
import { useShareRangeAnalysisEventsQuery } from "src/store/shareRangeAnalysisEvent";
import { getColumnData, getColumns, getRowData } from "./utils";
import { IShareRangeAnalysisEvent } from "src/store/shareRangeAnalysisEvent/types";
import { TABLE_SEARCH_PARAMS } from "src/constants/tableSearchParams";
import { useTableSearchParams } from "src/hooks/useTableSearchParams";
import { ScrollContainer } from "src/components/containers/ScrollContainer";

export type ShareRangeAnalysisEventsView =
  | "pending_approval"
  | "pending_delivery"
  | "recently_delivered"
  | "historical";

export const SHARE_RANGE_ANALYSIS_EVENTS_VIEW: {
  [key: string]: ShareRangeAnalysisEventsView;
} = {
  PENDING_APPROVAL: "pending_approval",
  PENDING_DELIVERY: "pending_delivery",
  RECENTLY_DELIVERED: "recently_delivered",
  HISTORICAL: "historical",
};

export function ShareRangeAnalysisEvents() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm();
  const view = (searchParams.get(TABLE_SEARCH_PARAMS.VIEW) ||
    SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL) as ShareRangeAnalysisEventsView;
  const { type } = useTableSearchParams();

  const {
    getSort,
    offset,
    limit,
    ordering,
    onChangePage,
    onChangeRows,
    onOrderingChange,
  } = useTablePagination();
  const { data, isFetching, isLoading } = useShareRangeAnalysisEventsQuery({
    offset,
    limit,
    ordering,
    type,
    tab: view || SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL,
  });

  const paginateProps = {
    onChangePage,
    onChangeRows,
    rowOptions: ROW_OPTIONS,
    count: data?.count || 0,
    page: Math.floor(offset / limit) + 1,
    rowsPerPage: limit,
  };

  const handleRowClick = (event: IShareRangeAnalysisEvent) => {
    const { id } = event;
    navigate(`/share-range-analysis-events/${id}/details`);
  };

  const handleSecondaryTabChange = (tab: string) => {
    const newParams = new URLSearchParams({
      view: tab,
    });
    setSearchParams(newParams);
  };

  const columns = useMemo(() => getColumns(view), [view]);
  const columnData = useMemo(() => {
    return getColumnData({
      getSort,
      onOrderingChange,
    });
  }, [getSort, onOrderingChange]);
  const rowData = getRowData(data?.results || [], handleRowClick);

  const filterList = (
    <FormProvider {...methods}>
      <TableMultiSelectFilter
        fieldName={TABLE_SEARCH_PARAMS.TYPE}
        label="Types"
        options={SHARE_RANGE_ANALYSIS_TYPE_OPTIONS}
        queryParameter={TABLE_SEARCH_PARAMS.TYPE}
        view={view}
      />
    </FormProvider>
  );

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Stack>
        <SecondaryTabs
          sx={{ mb: 8, mr: "auto" }}
          tabs={[
            {
              label: "Pending approval",
              value: SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL,
              icon:
                view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL ? (
                  <WatchLater style={{ color: "#1968FF" }} />
                ) : (
                  <WatchLaterOutlined />
                ),
            },
            {
              label: "Pending delivery",
              value: SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY,
              icon:
                view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY ? (
                  <Downloading style={{ color: "#1968FF" }} />
                ) : (
                  <DownloadingOutlined />
                ),
            },
            {
              label: "Recently delivered",
              value: SHARE_RANGE_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED,
              icon:
                view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED ? (
                  <Verified style={{ color: "#1968FF" }} />
                ) : (
                  <VerifiedOutlined />
                ),
            },
            {
              label: "Historical",
              value: SHARE_RANGE_ANALYSIS_EVENTS_VIEW.HISTORICAL,
              icon:
                view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.HISTORICAL ? (
                  <History style={{ color: "#1968FF" }} />
                ) : (
                  <HistoryOutlined />
                ),
            },
          ]}
          activeTab={view}
          clickHandler={handleSecondaryTabChange}
        />
      </Stack>
      <Table
        count={data?.count || 0}
        columnData={columnData}
        columns={columns}
        isFetching={isFetching}
        isLoading={isLoading}
        noResultsData={{
          title: "No Share Range Analysis Events found",
          subtitle:
            "Sorry, there are no Share Range Analysis Events that match your applied filters.",
        }}
        paginateProps={paginateProps}
        rows={rowData}
        tableMinWidth={1100}
        title={
          view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL
            ? "Pending Approval Share Range Analysis Events"
            : view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY
            ? "Pending Delivery Share Range Analysis Events"
            : view === SHARE_RANGE_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED
            ? "Recently Delivered Share Range Analysis Events"
            : "Historical Share Range Analysis Events"
        }
        titleVariant="subtitle2"
        toolbarInside
        toolbarCollapse={{
          Icon: FilterList,
          collapseContent: filterList,
          isFlat: true,
        }}
      />
    </ScrollContainer>
  );
}
